import type { FieldDefinition } from "@estelle/domain";

/**
 * The Products list's fields — what FR-CAT-9 asks the filter builder, the
 * column chooser and the sort control to offer for a product row.
 *
 * Order matters — the column chooser takes the first six as the default
 * set, so identifying fields come first: what the product is, what it is
 * worth, how covered it is for GST. Everything after exists to narrow the
 * list, not to open it.
 *
 * `priceRange`, `hsnCode` and `collections` are joins across variants and
 * collections. Both adapters filter on them — the API in SQL
 * (`PRODUCT_FIELDS` in `apps/api/src/routes/catalog.ts`), fixtures in
 * `projectProduct` — and the screen shows them from the variants it already
 * fetched for each row.
 *
 * `collections` declares no `options` here because a store's collections are
 * rows, not a fixed list: the section supplies them at runtime
 * (`fieldOptions` in `index.ts`, #308) and the filter builder offers them as
 * a picker. The key stays plural — it is stored in saved views and encoded
 * in shared links, and a product is in several collections at once.
 *
 * No stock on hand: stock belongs to the Inventory module (FR-CAT-3 and
 * FR-CAT-9 amended 2026-09-15), so Catalog describes what is sold and
 * Inventory how many there are.
 *
 * `priceRange` compares on the lowest variant price: "price over 800" is the
 * question people ask, and a range has no single value to compare.
 */
export const CATALOG_FIELDS: FieldDefinition[] = [
  {
    name: "thumbnail",
    label: "Image",
    type: "text",
    filterable: false,
  },
  { name: "title", label: "Title", type: "text", sortable: true },
  { name: "vendor", label: "Vendor", type: "text", sortable: true },
  {
    name: "productType",
    label: "Product type",
    type: "text",
    sortable: true,
  },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    // `unlisted` is not in Shopify's documented status enum but is what the
    // live store returns for a product published to no sales channel —
    // leaving it out would make a real row unfilterable by its own status.
    options: [
      { value: "active", label: "Active" },
      { value: "draft", label: "Draft" },
      { value: "archived", label: "Archived" },
      { value: "unlisted", label: "Unlisted" },
    ],
  },
  { name: "variantCount", label: "Variants", type: "number", sortable: true },
  // Sorts, like it filters, on the lowest variant price. Named `priceRange`
  // rather than `price` to read as what it is next to `VARIANT_FIELDS`' own
  // `price` — a range across several rows, not one.
  { name: "priceRange", label: "Price", type: "money", sortable: true },
  { name: "hasHsn", label: "HSN coverage", type: "boolean", sortable: true },
  { name: "tags", label: "Tags", type: "tag", sortable: true },
  { name: "collections", label: "Collection", type: "tag", sortable: true },
  { name: "hsnCode", label: "HSN code", type: "text", sortable: true },
  { name: "rating", label: "Rating", type: "number", sortable: true },
  { name: "reviewCount", label: "Reviews", type: "number", sortable: true },
];

/**
 * The HSN master's own fields (FR-CAT-10).
 *
 * ERP-owned, not mirrored — there is no vendor or status here because
 * Shopify has no idea a rate table exists. The fixture's own filter
 * projection for this list carries `code`, `description` and `gstRate` but
 * not `cessRate`; the same gap as above applies to it until that projection
 * is extended.
 */
export const HSN_FIELDS: FieldDefinition[] = [
  { name: "code", label: "Code", type: "text", sortable: true },
  { name: "description", label: "Description", type: "text", sortable: true },
  { name: "gstRate", label: "GST rate", type: "number", sortable: true },
  { name: "cessRate", label: "Cess rate", type: "number", sortable: true },
  { name: "productCount", label: "Products", type: "number", sortable: true },
];

/**
 * Missing HSN's fields (FR-CAT-11).
 *
 * A short, fixed set rather than the full `CATALOG_FIELDS` — this screen is
 * a worklist someone clears, not a general variant browser, so it offers
 * only what is needed to go find the variant in Shopify and set its code.
 * `variantTitle`, not `title`, so a column header reads "Variant" rather
 * than the ambiguous "Title" next to a `productTitle` column on the same
 * row.
 */
export const VARIANT_FIELDS: FieldDefinition[] = [
  { name: "sku", label: "SKU", type: "text", sortable: true },
  { name: "productTitle", label: "Product", type: "text", sortable: true },
  { name: "variantTitle", label: "Variant", type: "text", sortable: true },
  { name: "price", label: "Price", type: "money", sortable: true },
];
